import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import { haptic } from '../lib/telegram';
import type { MessageTemplate, TemplateKind, TemplateList } from '../types';
import { Button, Card, ErrorNotice, Field, Input, Modal, Select } from './ui';

/** Где заготовка пригодится. Порядок в списке, а не запрет. */
const KIND_LABELS: Record<TemplateKind, string> = {
  CLARIFY: 'Уточнение по заявке',
  ACCEPT: 'Принятие заявки',
  STAGE: 'Ход работы',
  DELIVERY: 'Выдача работы',
  OTHER: 'Прочее',
};

const KINDS = Object.keys(KIND_LABELS) as TemplateKind[];

/**
 * Библиотека заготовок. Художник пишет одни и те же слова десятки раз —
 * здесь они хранятся один раз, а в переменных подставляются имя заказчика,
 * номер заказа и остаток.
 */
export function TemplateManager() {
  const [editing, setEditing] = useState<MessageTemplate | 'new' | null>(null);
  const queryClient = useQueryClient();

  const templates = useQuery({
    queryKey: ['templates', 'plain'],
    queryFn: () => api<TemplateList>('/templates'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const items = templates.data?.items ?? [];

  return (
    <>
      <div className="section-heading">
        <h2>Шаблоны ответов</h2>
        <button onClick={() => setEditing('new')}>Добавить</button>
      </div>

      <Card className="stack stack--compact">
        {templates.error && <ErrorNotice error={templates.error} />}
        {remove.error && <ErrorNotice error={remove.error} />}

        {items.length === 0 && (
          <p className="muted">
            Заготовки для того, что вы пишете каждый раз: «пришлите референсы», «беру в работу»,
            «скетч готов».
          </p>
        )}

        {items.map((template) => (
          <div key={template.id} className="template-row">
            <div>
              <strong>{template.title}</strong>
              <p className="meta">{KIND_LABELS[template.kind]}</p>
              <p className="template-row__body">{template.body}</p>
            </div>
            <div className="template-row__actions">
              <button type="button" className="link-button" onClick={() => setEditing(template)}>
                Править
              </button>
              <button
                type="button"
                className="link-button"
                disabled={remove.isPending}
                onClick={() => remove.mutate(template.id)}
              >
                Удалить
              </button>
            </div>
          </div>
        ))}
      </Card>

      {editing && (
        <TemplateModal
          template={editing === 'new' ? null : editing}
          variables={templates.data?.variables ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function TemplateModal({
  template,
  variables,
  onClose,
}: {
  template: MessageTemplate | null;
  variables: { name: string; hint: string }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(template?.title ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [kind, setKind] = useState<TemplateKind>(template?.kind ?? 'OTHER');

  const save = useMutation({
    mutationFn: () =>
      api<MessageTemplate>(template ? `/templates/${template.id}` : '/templates', {
        method: template ? 'PATCH' : 'POST',
        body: { title: title.trim(), body: body.trim(), kind },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
  });

  return (
    <Modal title={template ? 'Шаблон' : 'Новый шаблон'} onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack stack--compact"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Название" hint="Видно только вам">
          <Input
            value={title}
            placeholder="Скетч готов"
            onChange={(event) => setTitle(event.target.value)}
          />
        </Field>

        <Field label="Где пригодится">
          <Select value={kind} onChange={(event) => setKind(event.target.value as TemplateKind)}>
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {KIND_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Текст">
          <textarea
            className="input input--textarea"
            rows={5}
            maxLength={2000}
            value={body}
            placeholder="{клиент}, скетч по заказу {заказ} готов — посмотрите в приложении."
            onChange={(event) => setBody(event.target.value)}
          />
        </Field>

        {/* Нажатие вставляет переменную: набирать фигурные скобки на телефоне
            неудобно, а промахнувшийся в имени шаблон молча не подставится. */}
        <div className="template-vars">
          {variables.map((variable) => (
            <button
              key={variable.name}
              type="button"
              className="tag-pill"
              title={variable.hint}
              onClick={() => setBody((current) => `${current}{${variable.name}}`)}
            >
              {`{${variable.name}}`}
            </button>
          ))}
        </div>

        {save.error && <ErrorNotice error={save.error} />}
        <Button type="submit" disabled={!title.trim() || !body.trim() || save.isPending}>
          {save.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </form>
    </Modal>
  );
}
